/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

import { StyledModal, useWindowSize, ErrorBoundary } from 'sarsaparilla';

const getCenteredCardIndex = (cardsContainer) => {
    const cardNodes = cardsContainer?.querySelectorAll('.shared-map-view-with-cards-li');
    if (!cardNodes?.length) {
        return null;
    }

    const viewportCenterX = cardsContainer.scrollLeft + cardsContainer.clientWidth / 2;
    let centeredCardIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    cardNodes.forEach((node, index) => {
        const cardCenterX = node.offsetLeft + node.offsetWidth / 2;
        const distance = Math.abs(cardCenterX - viewportCenterX);
        if (distance < closestDistance) {
            closestDistance = distance;
            centeredCardIndex = index;
        }
    });

    return centeredCardIndex;
};

function MapViewWithCards({
    cards = [],
    mapComponent,
    isOpen,
    onClose,
    onMapClick,
    isScrollingFromMap,
    selectedFeatureId,
}) {
    const { height } = useWindowSize();
    const cardScrollDebounceMs = 150;
    const scrollSuppressionMs = 600;

    // Set the required references to get the list scroll position and programmatically scroll it
    const cardsView = useRef(null);
    const lastScrolledFeatureIdRef = useRef(null);

    useLayoutEffect(() => {
        if (!isOpen) {
            lastScrolledFeatureIdRef.current = null;
            return undefined;
        }
        if (!selectedFeatureId || !cardsView.current) return undefined;
        if (lastScrolledFeatureIdRef.current === selectedFeatureId) return undefined;

        const targetIndex = cards.findIndex((card) => card?.key === selectedFeatureId);
        if (targetIndex < 0) return undefined;

        const cardNodes = cardsView.current.querySelectorAll(
            '.shared-map-view-with-cards-li'
        );
        const targetNode = cardNodes[targetIndex];
        if (!targetNode) return undefined;

        lastScrolledFeatureIdRef.current = selectedFeatureId;
        if (isScrollingFromMap) {
            isScrollingFromMap.current = true;
        }
        targetNode.scrollIntoView({ block: 'end' });
        const resetId = window.setTimeout(() => {
            if (isScrollingFromMap) {
                isScrollingFromMap.current = false;
            }
        }, scrollSuppressionMs);
        return () => window.clearTimeout(resetId);
    }, [isOpen, selectedFeatureId, cards, isScrollingFromMap]);

    const handleCardsScroll = useMemo(
        () =>
            debounce(() => {
                if (cardsView.current && !isScrollingFromMap?.current) {
                    const selectedItem = getCenteredCardIndex(cardsView.current);
                    if (selectedItem === null) return;

                    const featureId = cards[selectedItem]?.key;
                    if (featureId) {
                        onMapClick(featureId);
                    }
                }
            }, cardScrollDebounceMs),
        [cards, isScrollingFromMap, onMapClick]
    );

    useEffect(() => {
        return () => {
            handleCardsScroll.cancel();
        };
    }, [handleCardsScroll]);

    if (!isOpen) {
        return null;
    }

    return (
        <StyledModal
            size="full"
            isOpen={isOpen}
            onRequestClose={onClose}
            className="shared-map-view-with-cards"
            overlayClassName="shared-map-view-with-cards-overlay"
            contentClassName="shared-map-view-with-cards-content"
            gaTrackingId="305876291574"
            gaTrackingValue="Close Map View"
        >
            <div className="shared-map-view-with-cards-map">
                <ErrorBoundary
                    hasDefaultWrapperClass={false}
                    fallbackElement={
                        <div>
                            We apologize but maps for this location are not available at
                            this time.
                        </div>
                    }
                >
                    {mapComponent}
                </ErrorBoundary>
            </div>

            <div className="shared-ios-hack-thing" style={{ height }}>
                <div className="shared-map-view-with-cards-cards">
                    <ul
                        className="shared-map-view-with-cards-ul"
                        ref={cardsView}
                        onScroll={handleCardsScroll}
                    >
                        <div aria-hidden="true" className="shared-map-view-spacer" />
                        {cards.map((item, index) => {
                            return (
                                <li key={index} className="shared-map-view-with-cards-li">
                                    <div className="shared-map-view-li-inner">
                                        <div className="shared-map-view-card-wrap">
                                            {item}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                        <div aria-hidden="true" className="shared-map-view-spacer" />
                    </ul>
                </div>
            </div>
        </StyledModal>
    );
}

MapViewWithCards.propTypes = {
    cards: PropTypes.arrayOf(PropTypes.object),
    mapComponent: PropTypes.node,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onMapClick: PropTypes.func,
    isScrollingFromMap: PropTypes.object,
    selectedFeatureId: PropTypes.string,
};

export default MapViewWithCards;
